import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { marked } from 'marked';
import { useTheme } from '@/context/ThemeContext';
import PageSEO from '@/components/PageSEO';
import NewsletterSignup from '@/components/NewsletterSignup';
import { BlogHeader } from '@/pages/BlogPage';
import { ArrowLeft, ArrowRight, Clock, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const JSONLD_ID = 'blog-jsonld';

function useArticleJsonLd(post) {
  useEffect(() => {
    if (!post) return;
    const data = {
      '@context': 'https://schema.org', '@type': 'NewsArticle',
      headline: post.title, description: post.description,
      datePublished: post.date, dateModified: post.date,
      author: { '@type': 'Organization', name: post.author },
      publisher: { '@type': 'Organization', name: 'OmniView' },
      mainEntityOfPage: { '@type': 'WebPage', '@id': post.canonical },
      keywords: (post.tags || []).join(', '), articleSection: post.category,
    };
    let el = document.getElementById(JSONLD_ID);
    if (!el) {
      el = document.createElement('script');
      el.id = JSONLD_ID; el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', post.canonical || '');
    return () => { document.getElementById(JSONLD_ID)?.remove(); };
  }, [post]);
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { isDark } = useTheme();
  const [post, setPost] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    setState('loading');
    axios.get(`${API}/api/blog/posts/${slug}`)
      .then(({ data }) => { setPost(data); setState('ready'); })
      .catch(() => setState('missing'));
  }, [slug]);

  useArticleJsonLd(post);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950 text-zinc-100' : 'bg-white text-zinc-900'}`}>
      {post && <PageSEO title={post.title} description={post.description} keywords={(post.tags || []).join(', ')} />}
      <BlogHeader isDark={isDark} />
      <main id="main" className="max-w-3xl mx-auto px-4 sm:px-8 py-14">
        <Link to="/blog" data-testid="back-to-blog"
          className={`text-xs inline-flex items-center gap-1 mb-8 ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-800'}`}>
          <ArrowLeft size={13} /> All posts
        </Link>

        {state === 'loading' && (
          <div data-testid="post-loading" className="flex items-center gap-2 text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}
        {state === 'missing' && (
          <div data-testid="post-missing">
            <h1 className="text-4xl font-bold mb-3">Post not found</h1>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>That article has moved or never existed.</p>
          </div>
        )}

        {state === 'ready' && post && (
          <article data-testid="blog-article">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                style={{ background: 'rgba(0,212,255,.1)', color: '#0091b3' }}>{post.category}</span>
              <span className={`text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{post.date} · {post.author}</span>
              <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                <Clock size={11} /> {post.read_minutes} min read
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 leading-[1.08]">{post.title}</h1>
            {post.hero_stat && (
              <p className="text-base md:text-lg font-medium mb-10 pl-5 border-l-2" style={{ borderColor: '#00D4FF' }}>
                {post.hero_stat}
              </p>
            )}
            <div data-testid="blog-body" className={`blog-prose ${isDark ? 'blog-prose-dark' : ''}`}
              dangerouslySetInnerHTML={{ __html: marked.parse(post.body || '') }} />

            <div className={`mt-14 rounded-xl border p-8 ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`}>
              <NewsletterSignup isDark={isDark} source={`blog_${post.slug}`} />
            </div>

            <div className={`mt-10 pt-8 border-t flex flex-col sm:flex-row gap-4 justify-between ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              {post.older ? (
                <Link to={`/blog/${post.older.slug}`} data-testid="post-older"
                  className={`text-sm inline-flex items-center gap-2 ${isDark ? 'text-zinc-400 hover:text-zinc-200' : 'text-zinc-600 hover:text-zinc-900'}`}>
                  <ArrowLeft size={14} /> {post.older.title}
                </Link>
              ) : <span />}
              {post.newer && (
                <Link to={`/blog/${post.newer.slug}`} data-testid="post-newer"
                  className={`text-sm inline-flex items-center gap-2 sm:text-right ${isDark ? 'text-zinc-400 hover:text-zinc-200' : 'text-zinc-600 hover:text-zinc-900'}`}>
                  {post.newer.title} <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
